// src/components/InvestmentsGrid.js
import React, { useMemo, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import FileUpload from './FileUpload';
import { BarChart2, PlusCircle, Trash2, Download, HelpCircle, TrendingUp, Edit2, AlertCircle } from 'react-feather';
import { exportDataAsCsv } from '../utils/exportUtils';

function InvestmentsGrid({ rowData, onDataImported, formData, onFormChange, onAddItem, onDeleteItem, onGridEdit, totals }) {
  
  // --- AUTO-DETECT ADVANCED DATA ---
  const hasAdvancedData = (
    formData.disposedOfQOF ||
    formData.acquiredQOF ||
    (parseFloat(formData.capitalGainDistributions) || 0) > 0 ||
    (parseFloat(formData.capitalLossCarryoverShort) || 0) > 0 ||
    (parseFloat(formData.capitalLossCarryoverLong) || 0) > 0 ||
    (parseFloat(formData.schedD_line4_shortTermFormGains) || 0) !== 0 ||
    (parseFloat(formData.schedD_line5_shortTermK1Gains) || 0) !== 0 ||
    (parseFloat(formData.schedD_line11_longTermFormGains) || 0) !== 0 ||
    (parseFloat(formData.schedD_line12_longTermK1Gains) || 0) !== 0 ||
    (parseFloat(formData.collectiblesGain) || 0) > 0 ||
    (parseFloat(formData.unrecapturedSection1250Gain) || 0) > 0
  );

  const [showAdvanced, setShowAdvanced] = useState(hasAdvancedData);

  // Helper for nested/flat updates
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    onFormChange({ target: { name, value: parseFloat(value) || 0 } });
  };

  // Custom Renderer for the Delete Button
  const DeleteButtonRenderer = (params) => (
    <button 
      onClick={() => onDeleteItem(params.data.id)} 
      className="delete-cell-renderer" 
      title="Delete Row"
    >
      <Trash2 size={16} />
    </button>
  );

  // Column Definitions for AG Grid
  const columnDefs = useMemo(() => [
    { 
      field: 'description', 
      headerName: 'Description (Col a)', 
      minWidth: 180,
      editable: true
    },
    { 
      field: 'reportingStatus', 
      headerName: '1099-B Status', 
      cellEditor: 'agSelectCellEditor',
      cellEditorParams: {
        values: ['Basis Reported', 'Basis Not Reported', 'Not on 1099-B']
      },
      editable: true,
      minWidth: 160,
      headerTooltip: 'Determines which box is checked on Form 8949'
    },
    { 
      field: 'purchaseDate', 
      headerName: 'Acquired (Col b)', 
      maxWidth: 130,
      editable: true
    },
    { 
      field: 'saleDate', 
      headerName: 'Sold (Col c)', 
      maxWidth: 130,
      editable: true
    },
    { 
      field: 'saleProceeds', 
      headerName: 'Proceeds (Col d)', 
      type: 'numericColumn', 
      valueParser: 'Number(newValue)',
      editable: true
    },
    { 
      field: 'costBasis', 
      headerName: 'Cost Basis (Col e)', 
      type: 'numericColumn', 
      valueParser: 'Number(newValue)',
      editable: true
    },
    { 
      field: 'adjustmentCode', 
      headerName: 'Code (Col f)', 
      maxWidth: 100,
      editable: true,
      headerTooltip: 'e.g., W for Wash Sale, B for Basis Adjustment'
    },
    { 
      field: 'adjustmentAmount', 
      headerName: 'Adj. Amount (Col g)', 
      type: 'numericColumn', 
      valueParser: 'Number(newValue)',
      editable: true,
      headerTooltip: 'Enter Wash Sale disallowed loss as a POSITIVE number here.'
    },
    { 
      headerName: 'Gain / Loss (Col h)', 
      valueGetter: params => {
        const proceeds = parseFloat(params.data.saleProceeds) || 0;
        const cost = parseFloat(params.data.costBasis) || 0;
        const adj = parseFloat(params.data.adjustmentAmount) || 0;
        return proceeds - cost + adj;
      }, 
      type: 'numericColumn', 
      editable: false,
      cellStyle: params => {
        if (params.value > 0) return { color: 'green', fontWeight: 'bold' };
        if (params.value < 0) return { color: 'red' };
        return null;
      }
    },
    { 
      headerName: 'Term', 
      valueGetter: params => {
        if (!params.data.saleDate || !params.data.purchaseDate) return '';
        const diffTime = new Date(params.data.saleDate) - new Date(params.data.purchaseDate);
        const diffDays = diffTime / (1000 * 60 * 60 * 24);
        return diffDays > 365 ? 'Long-Term' : 'Short-Term';
      }, 
      editable: false, 
      maxWidth: 110 
    },
    { 
      headerName: '', 
      width: 60, 
      cellRenderer: DeleteButtonRenderer, 
      editable: false, 
      sortable: false, 
      filter: false,
      resizable: false
    }
  ], [onDeleteItem]);
  
  // Default Column Properties
  const defaultColDef = useMemo(() => ({ 
    resizable: true, 
    sortable: true, 
    flex: 1, 
    minWidth: 100,
    singleClickEdit: true // <--- ADD THIS LINE
  }), []);

  // Handle edits within the grid
  const handleCellEdit = (params) => {
    onGridEdit(params.data.id, params.colDef.field, params.newValue);
  };

  // Handle CSV Export
  const handleExport = () => {
    exportDataAsCsv(rowData, `investments-data-${new Date().getFullYear()}.csv`);
  };

  const formatMoney = (amount) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
  };

  return (
    <div className="grid-wrapper">
      {/* --- Header Section --- */}
      <div className="content-box-header">
        <h2><BarChart2 size={20} className="icon-orange" /> Investments & Capital Gains</h2>
        <div className="header-actions">
          <button onClick={onAddItem} className="add-item-button">
            <PlusCircle size={16} />
            <span>Add Investment</span>
          </button>
          <button onClick={handleExport} className="add-item-button" title="Export investments data as CSV">
            <Download size={16} />
          </button>
          <FileUpload onDataUploaded={onDataImported} />
        </div>
      </div>

      {/* --- Desktop Grid View --- */}
      <div className="ag-grid-container ag-theme-alpine">
        <AgGridReact 
          rowData={rowData} 
          columnDefs={columnDefs} 
          defaultColDef={defaultColDef} 
          onCellValueChanged={handleCellEdit} 
        />
      </div>

      {/* --- Carryover Summary Alert --- */}
      {totals && totals.newCapitalLossCarryover > 0 && (
        <div className="summary-card carryover" style={{ marginTop: '1.5rem', borderTopColor: '#f6923bff', backgroundColor: '#fff7efff' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
            <TrendingUp size={20} color="#f6ab3bff" />
            <h3 style={{ margin: 0, color: '#af511eff' }}>Carryover to Next Year</h3>
          </div>
          <p style={{ fontSize: '1rem', fontWeight: 'normal', color: '#8a541eff', whiteSpace: 'normal' }}>
            Based on your losses this year, you have reached the $3,000 deduction limit. 
            You will have a <strong>Capital Loss Carryover of {formatMoney(totals.newCapitalLossCarryover)}</strong> to use on next year's tax return.
          </p>
        </div>
      )}

      {/* --- NIIT Input (FIXED COLORS) --- */}
      <div className="form-group" style={{ marginTop: '1.5rem', padding: '1rem', backgroundColor: '#fff7ed', borderRadius: '0.5rem', border: '1px solid #fed7aa' }}>
        <div className="header-title-with-help" style={{ marginBottom: '0.5rem' }}>
          <label style={{ fontWeight: 600, color: '#9a3412' }}>Net Investment Income Tax (Form 8960)</label>
          <div className="has-tooltip header-help-icon" data-tooltip="You may owe this 3.8% tax if you have investment income and your MAGI is over $200k ($250k for married filing jointly).">
            <HelpCircle size={16} className="icon-orange" />
          </div>
        </div>
        <input 
          type="number" 
          name="netInvestmentIncomeTax" 
          value={formData?.netInvestmentIncomeTax ?? ''} 
          onChange={onFormChange} 
          placeholder="0" 
        />
      </div>

      {/* --- Mobile Card View (Hidden on Desktop) --- */}
      <div className="mobile-card-list">
        {rowData.map((row) => (
          <div key={row.id} className="mobile-card">
            <div className="mobile-card-header">
              <input 
                type="text" 
                className="mobile-card-input" 
                style={{ textAlign: 'left', fontWeight: 700, fontSize: '1rem' }} 
                value={row.description} 
                onChange={(e) => onGridEdit(row.id, 'description', e.target.value)} 
                placeholder="Investment Description"
              />
              <button 
                onClick={() => onDeleteItem(row.id)} 
                className="delete-card-button" 
                title="Delete Investment"
              >
                <Trash2 size={18} />
              </button>
            </div>
            <div className="mobile-card-row">
              <span className="mobile-card-label">Date Acquired</span>
              <input 
                type="date" 
                className="mobile-card-input" 
                value={row.purchaseDate} 
                onChange={(e) => onGridEdit(row.id, 'purchaseDate', e.target.value)} 
              />
            </div>
            <div className="mobile-card-row">
              <span className="mobile-card-label">Cost Basis</span>
              <input 
                type="number" 
                className="mobile-card-input" 
                value={row.costBasis} 
                onChange={(e) => onGridEdit(row.id, 'costBasis', parseFloat(e.target.value) || 0)} 
              />
            </div>
            <div className="mobile-card-row">
              <span className="mobile-card-label">Date Sold</span>
              <input 
                type="date" 
                className="mobile-card-input" 
                value={row.saleDate} 
                onChange={(e) => onGridEdit(row.id, 'saleDate', e.target.value)} 
              />
            </div>
            <div className="mobile-card-row">
              <span className="mobile-card-label">Sale Proceeds</span>
              <input 
                type="number" 
                className="mobile-card-input" 
                value={row.saleProceeds} 
                onChange={(e) => onGridEdit(row.id, 'saleProceeds', parseFloat(e.target.value) || 0)} 
              />
            </div>
            <div className="mobile-card-row">
              <span className="mobile-card-label">Wash Sale Adj. (Code W)</span>
              <input 
                type="number" 
                className="mobile-card-input" 
                style={{ color: '#ea580c' }}
                value={row.washSaleAdjustment} 
                onChange={(e) => onGridEdit(row.id, 'washSaleAdjustment', parseFloat(e.target.value) || 0)} 
              />
            </div>
          </div>
        ))}
      </div>

      {/* --- ADVANCED SCHEDULE D QUESTION --- */}
      <div className="dependent-card" style={{ marginTop: '2rem', marginBottom: '1.5rem' }}>
        <div className="form-group">
          <label style={{ fontWeight: 600, color: '#554333ff' }}>
            Do you have carryovers or special investment situations?
          </label>
          <p style={{ fontSize: '0.9rem', color: '#8b7c64ff', marginBottom: '0.75rem' }}>
            Answer <strong>Yes</strong> if you have Capital Loss Carryovers from last year, Qualified Opportunity Fund (QOF) transactions, or gains from Collectibles/Real Estate depreciation.
          </p>
          <div className="radio-group">
            <input 
              type="radio" 
              id="advanced_no" 
              checked={!showAdvanced} 
              onChange={() => setShowAdvanced(false)} 
            />
            <label htmlFor="advanced_no">No</label>

            <input 
              type="radio" 
              id="advanced_yes" 
              checked={showAdvanced} 
              onChange={() => setShowAdvanced(true)} 
            />
            <label htmlFor="advanced_yes">Yes</label>
          </div>
        </div>
      </div>

      {/* --- ADVANCED INPUTS (CONDITIONAL) --- */}
      {showAdvanced && (
            <div style={{ marginTop: '1rem', padding: '1.5rem', backgroundColor: '#fcfaf8ff', borderRadius: '0.5rem', border: '1px solid #f0e9e2ff', animation: 'fadeIn 0.3s' }}>

                {/* --- NEW: Line 7b Checkboxes & Amount --- */}
                <h5 style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>Schedule D Exceptions (Line 7b)</h5>
                
                <div className="form-group checkbox-group">
                    <input 
                        type="checkbox" 
                        id="scheduleDNotRequired"
                        name="scheduleDNotRequired" 
                        checked={formData.scheduleDNotRequired || false} 
                        onChange={(e) => onFormChange({ target: { name: 'scheduleDNotRequired', value: e.target.checked, type: 'checkbox' } })} 
                    />
                    <label htmlFor="scheduleDNotRequired">
                        Schedule D not required (Box 1)
                    </label>
                </div>

                <div className="form-group checkbox-group">
                    <input 
                        type="checkbox" 
                        id="includesChildsCapitalGain"
                        name="includesChildsCapitalGain" 
                        checked={formData.includesChildsCapitalGain || false} 
                        onChange={(e) => onFormChange({ target: { name: 'includesChildsCapitalGain', value: e.target.checked, type: 'checkbox' } })} 
                    />
                    <label htmlFor="includesChildsCapitalGain">
                        Includes child's capital gain/loss (Box 2)
                    </label>
                </div>

                {/* --- CONDITIONAL INPUT: Child's Gain Amount --- */}
                {formData.includesChildsCapitalGain && (
                    <div className="form-group" style={{ marginLeft: '1.8rem', marginTop: '0.5rem' }}>
                        <label style={{ fontSize: '0.9rem', color: '#554333ff' }}>Amount of Child's Gain (Form 8814)</label>
                        <div className="input-with-prefix">
                            <span className="prefix">$</span>
                            <input 
                                type="number" 
                                name="childCapitalGainAmount" 
                                value={formData.childCapitalGainAmount ?? ''} 
                                onChange={handleInputChange} 
                                placeholder="0.00"
                                style={{ width: '150px' }}
                            />
                        </div>
                        <small>This amount will be added to your Total Capital Gains (Line 7a).</small>
                    </div>
                )}

                {/* --- QOF QUESTIONS --- */}
                <h5 style={{ marginTop: 0, marginBottom: '1rem' }}>Qualified Opportunity Funds (QOF)</h5>
                <div className="form-group checkbox-group">
                    <input 
                        type="checkbox" 
                        id="disposedOfQOF"
                        name="disposedOfQOF" 
                        checked={formData.disposedOfQOF || false} 
                        onChange={(e) => onFormChange({ target: { name: 'disposedOfQOF', value: e.target.checked, type: 'checkbox' } })} 
                    />
                    <label htmlFor="disposedOfQOF">
                        Did you <strong>dispose</strong> of any investment(s) in a qualified opportunity fund during the tax year?
                    </label>
                </div>
                <div className="form-group checkbox-group">
                    <input 
                        type="checkbox" 
                        id="acquiredQOF"
                        name="acquiredQOF" 
                        checked={formData.acquiredQOF || false} 
                        onChange={(e) => onFormChange({ target: { name: 'acquiredQOF', value: e.target.checked, type: 'checkbox' } })} 
                    />
                    <label htmlFor="acquiredQOF">
                        Did you <strong>acquire</strong> any investment(s) in a qualified opportunity fund during the tax year?
                    </label>
                </div>

                <h5 style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>Mutual Fund Distributions</h5>
                <div className="form-group">
                    <label>Capital Gain Distributions (1099-DIV Box 2a)</label>
                    <input 
                        type="number" 
                        name="capitalGainDistributions" 
                        value={formData.capitalGainDistributions ?? ''} 
                        onChange={handleInputChange} 
                        placeholder="0"
                    />
                    <small>Reported on Schedule D, Line 13.</small>
                </div>
                
                <h5 style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>Capital Loss Carryover from 2024</h5>
                <div className="form-row">
                    <div className="form-group">
                        <label>Short-Term Carryover</label>
                        <input 
                            type="number" 
                            name="capitalLossCarryoverShort" 
                            value={formData.capitalLossCarryoverShort ?? ''} 
                            onChange={handleInputChange} 
                            placeholder="0"
                        />
                        <small>Enter loss from 2024 Schedule D, Line 6.</small>
                    </div>
                    <div className="form-group">
                        <label>Long-Term Carryover</label>
                        <input 
                            type="number" 
                            name="capitalLossCarryoverLong" 
                            value={formData.capitalLossCarryoverLong ?? ''} 
                            onChange={handleInputChange} 
                            placeholder="0"
                        />
                        <small>Enter loss from 2024 Schedule D, Line 14.</small>
                    </div>
                </div>

                <h5 style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>Gains from Other Forms & K-1s</h5>
                
                {/* Short-Term */}
                <div className="form-row">
                    <div className="form-group">
                        <label>Short-Term: Other Forms (Line 4)</label>
                        <input 
                            type="number" 
                            name="schedD_line4_shortTermFormGains" 
                            value={formData.schedD_line4_shortTermFormGains ?? ''} 
                            onChange={handleInputChange} 
                            placeholder="Form 6252, 4684, etc."
                        />
                    </div>
                    <div className="form-group">
                        <label>Short-Term: Schedule K-1 (Line 5)</label>
                        <input 
                            type="number" 
                            name="schedD_line5_shortTermK1Gains" 
                            value={formData.schedD_line5_shortTermK1Gains ?? ''} 
                            onChange={handleInputChange} 
                            placeholder="Partnerships, S-Corps"
                        />
                    </div>
                </div>

                {/* Long-Term */}
                <div className="form-row">
                    <div className="form-group">
                        <label>Long-Term: Other Forms (Line 11)</label>
                        <input 
                            type="number" 
                            name="schedD_line11_longTermFormGains" 
                            value={formData.schedD_line11_longTermFormGains ?? ''} 
                            onChange={handleInputChange} 
                            placeholder="Form 4797, 2439, etc."
                        />
                    </div>
                    <div className="form-group">
                        <label>Long-Term: Schedule K-1 (Line 12)</label>
                        <input 
                            type="number" 
                            name="schedD_line12_longTermK1Gains" 
                            value={formData.schedD_line12_longTermK1Gains ?? ''} 
                            onChange={handleInputChange} 
                            placeholder="Partnerships, S-Corps"
                        />
                    </div>
                </div>

                <h5 style={{ marginTop: '1.5rem', marginBottom: '1rem' }}>Special Gain Types</h5>
                <div className="form-row">
                    <div className="form-group">
                        <label>28% Rate Gain (Collectibles)</label>
                        <input 
                            type="number" 
                            name="collectiblesGain" 
                            value={formData.collectiblesGain ?? ''} 
                            onChange={handleInputChange} 
                            placeholder="0"
                        />
                        <small>Gains from art, coins, stamps, etc.</small>
                    </div>
                    <div className="form-group">
                        <label>Unrecaptured Section 1250 Gain</label>
                        <input 
                            type="number" 
                            name="unrecapturedSection1250Gain" 
                            value={formData.unrecapturedSection1250Gain ?? ''} 
                            onChange={handleInputChange} 
                            placeholder="0"
                        />
                        <small>Depreciation recapture from real estate sales.</small>
                    </div>
                </div>
            </div>
      )}

      {/* --- Manual Override Section --- */}
      {/* --- Manual Override Section (Fixed Colors) --- */}
      <div className="form-group" style={{ 
          marginTop: '1.5rem', 
          padding: '1rem', 
          backgroundColor: '#fff7ed', // Changed from #f8fafc
          borderRadius: '0.5rem', 
          border: '1px solid #fed7aa' // Changed from #e2e8f0
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.5rem' }}>
          {/* Changed icon-blue to icon-orange */}
          <Edit2 size={16} className="icon-orange" />
          <label style={{ fontWeight: 600, color: '#9a3412', margin: 0 }}>Manual Entry (Optional)</label>
        </div>
        <p style={{ fontSize: '0.85rem', color: '#c2410c', marginBottom: '0.75rem' }}>
          If you prefer to enter your total Capital Gain/Loss directly (e.g., from a consolidated 1099-B), enter it here. This will override the grid calculation.
        </p>
        <div className="form-row">
          <div className="form-group">
            <label>Net Capital Gain/Loss (Schedule D)</label>
            <input 
              type="number" 
              name="manualCapitalGains" 
              value={formData?.manualCapitalGains ?? ''} 
              onChange={onFormChange} 
              placeholder="0" 
              style={{ borderColor: '#fed7aa' }}
            />
          </div>
        </div>
      </div>

    </div>
  );
}

export default InvestmentsGrid;